import type { GMXNavLinkPanel } from './nav-link-panel'
import { swapAuthLinks } from './auth-link-swap'

interface RewardsReward {
    points?: number | string;
    tier?: string;
    status?: string;
}

interface RewardsData {
    reward?: RewardsReward;
}

// Grouping is validated rather than stripped so an ambiguous decimal comma ('1 234,56') is rejected.
const GROUPED_INTEGER = /^(\d+|\d{1,3}([,\u0020\u00a0\u202f\u2009]\d{3})+)$/
const GROUP_SEPARATORS = /[,\u0020\u00a0\u202f\u2009]/g

function parseRewardsPointsString(raw: string): number | null {
    const trimmed = raw.trim();
    if (!GROUPED_INTEGER.test(trimmed)) {
        return null;
    }
    return Number(trimmed.replace(GROUP_SEPARATORS, ''));
}

export class GMXAccount extends HTMLElement {

    accountButton: HTMLAnchorElement
    signInButton: HTMLAnchorElement
    signOutButton: HTMLButtonElement
    linkPanel: GMXNavLinkPanel | null
    accountUrl: string

    private _panelStateObserver: MutationObserver

    // Bumped on every auth update so a late rewards response can be recognised as stale.
    private _rewardsRequestGeneration = 0

    private _onGBPEAuthStateUpdated = (): void => {
        this.syncGBPEAuthUI();
    };

    private _onAccountClick = (event: MouseEvent): void => {
        this._handleAccountClick(event);
    };

    private _onAccountKeydown = (event: KeyboardEvent): void => {
        this._handleAccountKeydown(event);
    };

    private _onSignInClick = (event: Event): void => {
        this._handleSignInClick(event);
    };

    private _onSignOutClick = (event: Event): void => {
        this._handleSignOutClick(event);
    };

    connectedCallback(): void {
        this.accountButton = this.querySelector('.gmx-account-btn')
        this.signInButton = this.querySelector('.gmx-sign-in');
        this.signOutButton = this.querySelector('.log-out');
        this.linkPanel = this.querySelector('gmx-nav-link-panel');
        this.accountUrl = this.dataset.accountUrl;

        if (!this.accountButton) {
            window.console.error('ERROR: gmx-account is missing its .gmx-account-btn trigger; skipping initialisation.');
            return;
        }

        this.accountButton.addEventListener('click', this._onAccountClick);
        this.accountButton.addEventListener('keydown', this._onAccountKeydown);
        if (this.signInButton) {
            this.signInButton.addEventListener('click', this._onSignInClick);
        }
        if (this.signOutButton) {
            this.signOutButton.addEventListener('click', this._onSignOutClick);
        }

        if (this.linkPanel) {
            this._panelStateObserver = new MutationObserver(() => this._syncPanelState());
            this._panelStateObserver.observe(this.linkPanel, { attributes: true, attributeFilter: ['class'] });
        }

        this.initializeGBPEAuth()
    }

    disconnectedCallback(): void {
        this._rewardsRequestGeneration++;
        this._panelStateObserver?.disconnect();
        this.accountButton?.removeEventListener('click', this._onAccountClick);
        this.accountButton?.removeEventListener('keydown', this._onAccountKeydown);
        this.signInButton?.removeEventListener('click', this._onSignInClick);
        this.signOutButton?.removeEventListener('click', this._onSignOutClick);
        window.removeEventListener('gbpe:authStateUpdated', this._onGBPEAuthStateUpdated);
    }

    _handleSignInClick(event: Event): void {
        event.preventDefault();
        if (window.gbpe && window.gbpe.auth && typeof window.gbpe.auth.loginRedirect === 'function') {
            window.gbpe.auth.loginRedirect();
        }
    }

    _handleSignOutClick(event: Event): void {
        event.preventDefault();
        if (window.gbpe && window.gbpe.auth && typeof window.gbpe.auth.logout === 'function') {
            window.gbpe.auth.logout();
        }
    }

    // A modified click follows the anchor so the account page can open in a new tab.
    _handleAccountClick(event: MouseEvent): void {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
            return;
        }
        event.preventDefault();
        this._activateAccount();
    }

    _handleAccountKeydown(event: KeyboardEvent): void {
        if (event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            this._activateAccount();
        }
    }

    private _activateAccount(): void {
        if (this.linkPanel) {
            this.linkPanel.toggle();
            this._syncPanelState();
        } else {
            window.location.href = this.accountUrl;
        }
    }

    private _syncPanelState(): void {
        if (!this.linkPanel) {
            return;
        }
        const isOpen = this.linkPanel.classList.contains('open');
        this.accountButton.classList.toggle('active', isOpen);
        this.accountButton.setAttribute('aria-expanded', String(isOpen));
    }

    initializeGBPEAuth(): void {
        this.loadGBPEScript().then(() => {
            if (!this.isConnected) {
                return;
            }
            if (window.gbpe && window.gbpe.auth) {
                this.syncGBPEAuthUI();
                window.addEventListener('gbpe:authStateUpdated', this._onGBPEAuthStateUpdated);
            } else {
                this.displayLoggedOut();
            }
        }).catch(() => {
            this.displayLoggedOut();
        });
    }

    syncGBPEAuthUI(): void {
        if (!window.gbpe?.auth) {
            return;
        }
        const generation = ++this._rewardsRequestGeneration;
        if (window.gbpe.auth.currentAuthState === 1) {
            this.displayLoggedIn();
            // Covers nav markup that loaded before GBPE resolved the session.
            swapAuthLinks();
            Promise.allSettled([
                window.gbpe.user.profile,
                window.gbpe.user.rewards
            ]).then(results => {
                if (generation !== this._rewardsRequestGeneration) {
                    return;
                }
                if (results[0].status !== 'fulfilled') {
                    console.error('Error fetching user profile:', results[0].reason);
                }
                const rewardsData = results[1].status === 'fulfilled' ? results[1].value.data : null;
                if (results[1].status !== 'fulfilled') {
                    console.error('Error fetching user rewards:', results[1].reason);
                }
                this.updateRewardsTag(rewardsData);
            });
        } else {
            this.displayLoggedOut();
        }
    }

    loadGBPEScript(): Promise<void> {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = this.dataset.gbpeScript;
            script.async = true;
            script.defer = true;
            script.setAttribute('data-use-data-attributes', this.dataset.useDataAttributes || 'true');
            script.setAttribute('data-auth-login-brand-override', this.dataset.authLoginBrandOverride || '');
            script.setAttribute('data-language', this.dataset.language || 'en');
            script.setAttribute('data-country', this.dataset.country || 'us');
            script.setAttribute('data-environment', this.dataset.environment || 'prod');
            script.onload = () => {
                resolve();
            }
            script.onerror = () => {
                reject();
            }
            document.head.appendChild(script);
        });
    }

    // Panel-scoped: older authored markup also emits a tag on the account trigger.
    updateRewardsTag(rewardsData: RewardsData | null): void {
        const points = this.getRewardsPoints(rewardsData);
        const rewardsTag = (this.linkPanel ?? this).querySelector<HTMLElement>('.gmx-account-rewards');
        if (!rewardsTag) {
            return;
        }
        if (points === null) {
            rewardsTag.textContent = '';
            rewardsTag.classList.add('hidden');
            return;
        }
        rewardsTag.textContent = `${points.toLocaleString('en-US')} pts`;
        rewardsTag.classList.remove('hidden');
    }

    private getRewardsPoints(rewardsData: RewardsData | null): number | null {
        const raw = rewardsData?.reward?.points;
        if (raw === undefined || raw === null) {
            return null;
        }

        let points: number | null = null;
        if (typeof raw === 'number') {
            points = raw;
        } else if (typeof raw === 'string') {
            points = parseRewardsPointsString(raw);
        }

        // Safe integer, not merely finite: past 2^53 the value is silently rounded.
        if (points === null || !Number.isSafeInteger(points) || points < 0) {
            window.console.warn('gmx-account: ignoring unusable rewards points from GBPE:', raw);
            return null;
        }
        return points;
    }

    displayLoggedIn(): void {
        this.accountButton.classList.remove('hidden');
        this.signInButton?.classList.add('hidden');
    }

    displayLoggedOut(): void {
        this.accountButton.classList.add('hidden');
        this.signInButton?.classList.remove('hidden');
        this.updateRewardsTag(null);
    }
}
if (!customElements.get('gmx-account')) {
    customElements.define('gmx-account', GMXAccount)
}
